import {
  FileDownloadOutlined,
  InsightsOutlined,
  MoreVert,
  OpenInNewOutlined,
  PlayArrowOutlined,
  PlaylistRemoveOutlined,
  ReplayOutlined,
  StopCircleOutlined,
} from '@mui/icons-material'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import PqPhaseChip from '@/features/prequalification/localControl/components/PqPhaseChip'
import ResourceLink from '@/features/prequalification/localControl/components/ResourceLink'
import type { ResourceSetTestActions } from '@/features/prequalification/localControl/components/resourceSetTestActions'
import { resourceKey } from '@/features/prequalification/localControl/components/resourceSetTestActions'
import type { LocalPqRun, PqTestName, ResourceSetMember } from '@/features/prequalification/localControl/types'
import { makeResourceDashboardLink } from '@/features/prequalification/localControl/utils/coralogixLinks'
import { phaseChipTooltip } from '@/features/prequalification/localControl/utils/phasePresentation'
import { makeTemporalWorkflowLink } from '@/features/prequalification/localControl/utils/temporalLinks'
import { isTerminalPhase } from '@/features/prequalification/types'

type TestResourceRowProps = {
  resource: ResourceSetMember
  testName: PqTestName
  run: LocalPqRun | null
  actions: ResourceSetTestActions
}

/**
 * A single (resource, test) row revealed when a test is expanded: phase chip,
 * per-resource run/stop, result CSV and clear-queue. Resources with no run yet
 * show a "Queue" affordance and no phase.
 */
function TestResourceRow({ resource, testName, run, actions }: Readonly<TestResourceRowProps>) {
  const { t } = useTranslation()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  const isActive = run !== null && !isTerminalPhase(run.phase)
  const isPending =
    (run !== null && actions.pendingKeys.has(run.id)) ||
    actions.pendingKeys.has(resourceKey(resource.resourceId, testName))

  return (
    <Stack
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <ResourceLink resource={resource} />
      </Stack>

      <Stack sx={{ width: 124, alignItems: 'flex-start' }}>
        {run ? (
          <PqPhaseChip phase={run.phase} size="small" tooltipTitle={phaseChipTooltip(run.phase, run.startAt, t)} />
        ) : (
          <Typography color="text.secondary" variant="caption">
            {t('prequalifications.resource_set.test.not_run')}
          </Typography>
        )}
      </Stack>

      <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
        {isActive ? (
          <Tooltip title={t('prequalifications.resource_set.runs.action.stop')}>
            <span>
              <IconButton
                data-testid="resource-stop-button"
                disabled={isPending}
                onClick={() => actions.onStopResource(run!.id)}
              >
                <StopCircleOutlined />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              run
                ? t('prequalifications.resource_set.runs.action.run_again')
                : t('prequalifications.resource_set.test.start')
            }
          >
            <span>
              <IconButton
                data-testid="resource-run-button"
                disabled={isPending}
                onClick={() => actions.onRunResource(resource.resourceId, testName, null)}
              >
                {run ? <ReplayOutlined /> : <PlayArrowOutlined />}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title={t('prequalifications.resource_set.runs.action.download_csv')}>
          <span>
            <IconButton
              data-testid="resource-download-button"
              disabled={isPending || !run}
              onClick={() => run && actions.onDownloadResource(run.id)}
            >
              <FileDownloadOutlined />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t('prequalifications.resource_set.runs.action.clear_queue')}>
          <span>
            <IconButton
              data-testid="resource-clear-queue-button"
              disabled={isPending}
              onClick={() => actions.onClearResource(resource.resourceId)}
            >
              <PlaylistRemoveOutlined />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t('prequalifications.resource_set.runs.action.menu')}>
          <span>
            <IconButton
              aria-label={t('prequalifications.resource_set.runs.action.menu')}
              data-testid="resource-menu-button"
              onClick={(event) => setMenuAnchor(event.currentTarget)}
            >
              <MoreVert />
            </IconButton>
          </span>
        </Tooltip>

        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            component="a"
            href={makeResourceDashboardLink(resource.resourceId)}
            rel="noopener noreferrer"
            target="_blank"
            onClick={() => setMenuAnchor(null)}
          >
            <ListItemIcon>
              <InsightsOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('prequalifications.resource_set.runs.action.dashboard')}</ListItemText>
          </MenuItem>

          {run && (
            <MenuItem
              component="a"
              href={makeTemporalWorkflowLink(run.id)}
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => setMenuAnchor(null)}
            >
              <ListItemIcon>
                <OpenInNewOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('prequalifications.resource_set.runs.action.troubleshoot')}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Stack>
    </Stack>
  )
}

export default TestResourceRow
